import { trackPageEngagement, PageEngagementProperties } from './events';

/**
 * Page engagement tracker: measures how long each page is open and, within
 * that, how much time the user was actively interacting (pointer/keyboard/
 * scroll within the idle timeout, tab visible). Distinguishes a page being
 * read from one left open in a background tab.
 *
 * Framework-free and dependency-injected (clock, track fn) so it is unit
 * testable without a browser.
 */

export type EngagementTrackFn = (
  properties: PageEngagementProperties,
  reason: EngagementFlushReason
) => void;

export type EngagementFlushReason = 'route_change' | 'page_hide' | 'manual';

export interface EngagementTrackerOptions {
  /** No interaction for this long counts as idle. Default 60s. */
  idleTimeoutMs?: number;
  /** How often active time is accumulated. Default 5s. */
  tickMs?: number;
  /** Flushes shorter than this are skipped (navigation noise). Default 1s. */
  minDurationMs?: number;
  /** Activity signal throttle. Default 1s. */
  activityThrottleMs?: number;
  now?: () => number;
  track?: EngagementTrackFn;
}

export interface EngagementTracker {
  start(path: string): void;
  /** Flush the current page and begin tracking the next one. */
  handleRouteChange(path: string): void;
  /** Final flush (tab close). */
  flush(reason?: EngagementFlushReason): void;
  stop(): void;
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

export const createEngagementTracker = (
  options: EngagementTrackerOptions = {}
): EngagementTracker => {
  const {
    idleTimeoutMs = 60_000,
    tickMs = 5_000,
    minDurationMs = 1_000,
    activityThrottleMs = 1_000,
    now = () => Date.now(),
    track = (properties) => trackPageEngagement(properties),
  } = options;

  let path: string | null = null;
  let enteredAt = 0;
  let lastActivityAt = 0;
  let activeMs = 0;
  let lastSignalAt = 0;
  let running = false;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const isDocumentVisible = () =>
    typeof document === 'undefined' || document.visibilityState === 'visible';

  const markActivity = () => {
    const t = now();
    if (t - lastSignalAt < activityThrottleMs) return;
    lastSignalAt = t;
    lastActivityAt = t;
  };

  const isActive = () => {
    if (!isDocumentVisible()) return false;
    return now() - lastActivityAt < idleTimeoutMs;
  };

  const flush = (reason: EngagementFlushReason = 'manual') => {
    if (path === null) return;
    const durationMs = now() - enteredAt;
    if (durationMs < minDurationMs) return;

    track(
      {
        path,
        duration_ms: durationMs,
        active_ms: activeMs,
        idle_ms: Math.max(0, durationMs - activeMs),
        flush_reason: reason,
      },
      reason
    );
  };

  const beginPage = (nextPath: string) => {
    path = nextPath;
    enteredAt = now();
    lastActivityAt = enteredAt;
    activeMs = 0;
  };

  const handleRouteChange = (nextPath: string) => {
    // Auto-start on the first call so callers only ever need this method.
    if (!running) {
      start(nextPath);
      return;
    }
    flush('route_change');
    beginPage(nextPath);
  };

  const attachListeners = () => {
    if (typeof window === 'undefined') return;
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true })
    );
    window.addEventListener('focus', markActivity);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', markActivity);
    }
    window.addEventListener('pagehide', handlePageHide);
  };

  const detachListeners = () => {
    if (typeof window === 'undefined') return;
    ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActivity));
    window.removeEventListener('focus', markActivity);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', markActivity);
    }
    window.removeEventListener('pagehide', handlePageHide);
  };

  const handlePageHide = () => {
    flush('page_hide');
  };

  const start = (initialPath: string) => {
    if (running) return;
    running = true;
    beginPage(initialPath);
    attachListeners();
    intervalId = setInterval(() => {
      if (isActive()) {
        activeMs += tickMs;
      }
    }, tickMs);
  };

  const stop = () => {
    if (!running) return;
    running = false;
    flush('manual');
    detachListeners();
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    path = null;
  };

  return { start, handleRouteChange, flush, stop };
};
